import { vestResolver } from '@hookform/resolvers/vest'
import { FormStep } from '@sec-loans-ui/loan-offer/steps/FormStep'
import { LoanOfferReason } from '@shared/sec-loans'
import { useWizardForm } from '@ui-common/hooks'
import { YesNo } from '@ui-common/types'
import { formatFinnishCurrencyValue, toNumber } from '@ui-common/utils'
import { ExternalLink } from '@ui-components/actions/ExternalLink'
import { LooseFormSectionContent } from '@ui-components/containers/LooseSectionContent'
import { MediumSectionContent } from '@ui-components/containers/MediumSectionContent'
import { Notification } from '@ui-components/containers/Notification'
import { Divider } from '@ui-components/decorative/Divider'
import { DataSummary, DataSummaryItem } from '@ui-components/form/DataSummary'
import { FormRadioGroup } from '@ui-components/form/FormRadioGroup'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { COMMON_PLACEHOLDER } from '@ui-components/global/constants'
import { TwoColumnLayout } from '@ui-components/layout/TwoColumnLayout'
import { SecondaryHeading } from '@ui-components/typography/headings/SecondaryHeading'
import { AdditionalText, LeadText } from '@ui-components/typography/Typography'
import { FormattedMessage, useIntl } from 'react-intl'

import { ReservationFooter } from '../../components/ReservationFooter'
import { LoanOfferRecommendationFormData } from '../../wizard/types'
import { useExistingLoanOfferContext } from '../LoanOfferContext'
import { createFormValidator } from './formValidator'
import { getInvestmentReturns, LOAN_OFFER_SAVINGS_ANNUAL_INTEREST_RATE } from './recommendationStepUtils'

export const RecommendationStep = () => {
  const intl = useIntl()
  const { proceed, loanOffer, wizardData } = useExistingLoanOfferContext()

  const methods = useWizardForm<LoanOfferRecommendationFormData>({
    mode: 'onChange',
    defaultValues: wizardData?.recommendationInfo,
    resolver: vestResolver(createFormValidator(intl.formatMessage)),
  })

  const {
    register,
    watch,
    formState: { errors, isValid },
    handleSubmit,
  } = methods

  const onSubmit = (formData: LoanOfferRecommendationFormData) => {
    const { isInterestedInSavings } = formData
    proceed(isInterestedInSavings ? { recommendationInfo: formData } : {})
  }

  const longestLoanTimeMonths = Math.max(
    ...loanOffer.offeredLoans.map((item: LoanOfferReason) => item.loanPeriodMonths),
  )
  const shouldShowLoanProtectionRecommendation = !loanOffer.offeredLoans.some(
    (offer: LoanOfferReason) => offer.protections.length > 0,
  )
  const isInterestedInSavingsValue = watch('isInterestedInSavings')
  const monthlySavingsAmount = watch('monthlySavingsAmount')

  const interestRate = formatFinnishCurrencyValue(LOAN_OFFER_SAVINGS_ANNUAL_INTEREST_RATE * 100, 1, 1)
  const threeYearsInMonths = 36
  const tenYearsInMonths = 120

  const savings = getInvestmentReturns(toNumber(monthlySavingsAmount), longestLoanTimeMonths)

  const shouldShowSavingsFor3Years = longestLoanTimeMonths > threeYearsInMonths
  const shouldShowSavingsFor10Years = longestLoanTimeMonths > tenYearsInMonths

  const savingsOptions = [
    {
      key: YesNo.Yes,
      label: intl.formatMessage({ id: 'loan-offer-recommendation-savings-yes-label' }),
      description: intl.formatMessage({ id: 'loan-offer-recommendation-savings-yes-description' }),
    },
    {
      key: YesNo.No,
      label: intl.formatMessage({ id: 'common-no-me' }),
    },
  ]

  const getValueOrPlaceholder = (value: string | undefined) => {
    return isValid && value ? value : COMMON_PLACEHOLDER
  }

  return (
    <FormStep
      allowBack={true}
      subHeader={intl.formatMessage({ id: 'loan-offer-recommendation-step-heading' })}
      leadText={intl.formatMessage({ id: 'loan-offer-recommendation-step-lead-text' })}
      formMethods={methods}
      onSubmit={handleSubmit(onSubmit)}
      customFooter={
        <ReservationFooter loanOfferMode={loanOffer.loanOfferMode} reservationLink={loanOffer.reservationLink} />
      }
      disableDirtyCheck={true}
    >
      <LooseFormSectionContent>
        <TwoColumnLayout
          leftContent={
            <SecondaryHeading header={intl.formatMessage({ id: 'loan-offer-recommendation-savings-title' })} />
          }
          rightContent={
            <FormRadioGroup
              label={intl.formatMessage({ id: 'loan-offer-recommendation-savings-label' })}
              description={intl.formatMessage({ id: 'loan-offer-recommendation-savings-description' })}
              options={savingsOptions}
              {...register('isInterestedInSavings')}
            >
              {isInterestedInSavingsValue === YesNo.Yes && (
                <>
                  <FormTextInput
                    loan-offer-recommendation-savings-amount-label={true}
                    label={intl.formatMessage({ id: 'loan-offer-recommendation-savings-amount-label' })}
                    description={intl.formatMessage({
                      id: 'loan-offer-recommendation-savings-amount-description',
                    })}
                    inputMode="numeric"
                    error={errors.monthlySavingsAmount?.message}
                    {...register('monthlySavingsAmount')}
                  />
                  <DataSummary>
                    {shouldShowSavingsFor3Years && (
                      <DataSummaryItem
                        label={intl.formatMessage({ id: 'loan-offer-recommendation-savings-in3years-label' })}
                        value={getValueOrPlaceholder(savings?.valueFor3Years)}
                        id="savingsFor3Years"
                      />
                    )}
                    {shouldShowSavingsFor10Years && (
                      <DataSummaryItem
                        label={intl.formatMessage({ id: 'loan-offer-recommendation-savings-in10years-label' })}
                        value={getValueOrPlaceholder(savings?.valueFor10Years)}
                        id="savingsFor10Years"
                      />
                    )}
                    <DataSummaryItem
                      label={intl.formatMessage({ id: 'loan-offer-recommendation-savings-longest-loan-label' })}
                      value={getValueOrPlaceholder(savings?.valueForLongestLoan)}
                      id="savingsForLongestLoan"
                    >
                      <DataSummaryItem
                        label={intl.formatMessage({ id: 'loan-offer-recommendation-savings-amount-saved-label' })}
                        value={getValueOrPlaceholder(savings?.savingsForLongestLoan)}
                        id="savingsForLongestLoan-invested"
                      />
                      <DataSummaryItem
                        label={intl.formatMessage({ id: 'loan-offer-recommendation-savings-earned-label' })}
                        value={getValueOrPlaceholder(savings?.profitForLongestLoan)}
                        id="savingsForLongestLoan-interest"
                      />
                    </DataSummaryItem>
                  </DataSummary>
                  <Divider />
                  <MediumSectionContent>
                    <AdditionalText>
                      <FormattedMessage
                        id="loan-offer-recommendation-savings-expected-return"
                        values={{ interestRate }}
                      />
                    </AdditionalText>
                    <Notification
                      header={intl.formatMessage({ id: 'loan-offer-recommendation-savings-notification-header' })}
                      status="info"
                    >
                      <FormattedMessage id="loan-offer-recommendation-savings-notification-text" />
                    </Notification>
                    <AdditionalText>
                      <FormattedMessage id="loan-offer-recommendation-savings-disclaimer" values={{ interestRate }} />
                    </AdditionalText>
                  </MediumSectionContent>
                </>
              )}
            </FormRadioGroup>
          }
        />
        {shouldShowLoanProtectionRecommendation && (
          <>
            <Divider />
            <TwoColumnLayout
              leftContent={
                <SecondaryHeading header={intl.formatMessage({ id: 'loan-offer-recommendation-protections-title' })} />
              }
              rightContent={
                <LeadText>
                  <FormattedMessage
                    id="loan-offer-recommendation-protections-text"
                    values={{
                      protectionsLink: (
                        <ExternalLink
                          href={intl.formatMessage({ id: 'loan-offer-recommendation-protections-link-url' })}
                        >
                          {intl.formatMessage({ id: 'loan-offer-recommendation-protections-link-text' })}
                        </ExternalLink>
                      ),
                    }}
                  />
                </LeadText>
              }
            />
          </>
        )}
      </LooseFormSectionContent>
    </FormStep>
  )
}
